import React, { useState, useEffect } from "react";
import { Building, Save, CheckCircle2, Globe, Mail, MapPin, Award, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const InstitutionProfilePage: React.FC = () => {
  const [profile, setProfile] = useState({
    name: "Corvenio University of Engineering & Technology",
    code: "CUET-2026",
    type: "University",
    affiliated_to: "State Technical Board",
    address: "Campus Drive, Innovation Park, Tech City",
    contact_email: "admin@corvenio.edu",
    contact_phone: "+91 9876543210",
    accreditation: "NAAC Grade A+ (3.65 CGPA) | Autonomous",
    website: "https://corvenio.edu",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("institution");
      if (res.data) {
        setProfile((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load institutional profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("institution", profile);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save institutional profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading institutional profile...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-6">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Building className="w-5 h-5 text-indigo-400" />
          <div>
            <h2 className="text-base font-bold text-slate-100">Institutional Profile & Accreditation</h2>
            <p className="text-slate-400 text-[11px]">Primary organizational identity, accreditation scores, and contact information.</p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Profile Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Institution Full Name
          </label>
          <input
            type="text"
            value={profile.name}
            onChange={(e) => {
              setSaved(false);
              setProfile({ ...profile, name: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-semibold"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Short Code / Abbreviation
          </label>
          <input
            type="text"
            value={profile.code}
            onChange={(e) => {
              setSaved(false);
              setProfile({ ...profile, code: e.target.value });
            }}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono font-bold text-indigo-400"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Accreditation & NIRF Rating Details
          </label>
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-400 shrink-0" />
            <input
              type="text"
              value={profile.accreditation}
              placeholder="e.g. NAAC Grade A+ (3.65 CGPA) | Autonomous"
              onChange={(e) => {
                setSaved(false);
                setProfile({ ...profile, accreditation: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-semibold"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Official Email Address
          </label>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              type="email"
              value={profile.contact_email}
              onChange={(e) => {
                setSaved(false);
                setProfile({ ...profile, contact_email: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Website URL
          </label>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              type="text"
              value={profile.website}
              placeholder="https://institution.edu"
              onChange={(e) => {
                setSaved(false);
                setProfile({ ...profile, website: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs font-mono"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-[11px] font-semibold text-slate-300 mb-1">
            Campus Address
          </label>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              type="text"
              value={profile.address}
              placeholder="Full campus physical address..."
              onChange={(e) => {
                setSaved(false);
                setProfile({ ...profile, address: e.target.value });
              }}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
